import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom'; // To get article ID/slug from URL

// Import Components
import ArticleHeader from './components/articleHeader';
import AuthorInfo from './components/articleInfo';
import ArticleBody from './components/articleBody';
import QuickLinks from './components/quickLinks';
import References from './components/reference';
import RecommendedArticles from './components/recommendedArticles';

// Import Styles
import { layout,commonStyles, colors  } from './articleMinimalStyles';

// --- Mock Data Fetching (Replace with actual fetching) ---
// Find the specific article data based on slug/ID
const getArticleData = async (slug) => {
  // Simulate fetching article details
  // console.log"Fetching article:", slug);
  // Find article in a larger dataset or fetch from API
  const sampleArticle = {
        id: 'ai-plastic-analysis',
        slug: 'ai-plastic-analysis',
        title: 'Putting AI to work: better, faster, more accurate analysis of plastic samples',
        category: 'Technology',
        date: 'April 2, 2025',
        featuredImage: { src: 'https://via.placeholder.com/1000x500?text=AI+Plastic+Analysis', alt: 'AI analyzing plastic samples' },
        author: { name: 'Dr. Joseph Levermore', title: 'Founder', avatar: 'https://via.placeholder.com/100?text=SC' },
        // Example HTML content WITH IDs for QuickLinks
        content: `
          <p>Our latest AI models are revolutionizing how researchers and companies identify and categorise different plastic types with unprecedented accuracy.</p>
          <h2 id="introduction">Introduction</h2>
          <p>The challenge of identifying microplastics, especially weathered or complex samples, requires advanced analytical techniques. Traditional methods often struggle with speed and accuracy. This article explores how Plastic Analytics leverages Artificial Intelligence (AI) to overcome these limitations.</p>
          <img src="https://via.placeholder.com/700x400?text=In+Content+Image" alt="Analysis Graph" style="max-width: 100%; height: auto; margin: 1.5em 0; border-radius: 4px;" />
          <h2 id="methodology">Methodology</h2>
          <p>Our approach combines high-resolution spectral data (FTIR & Raman) with sophisticated machine learning algorithms. Key steps include:</p>
          <ul>
            <li>Spectral pre-processing to remove noise.</li>
            <li>Feature extraction using convolutional neural networks (CNNs).</li>
            <li>Classification against our extensive spectral database.</li>
          </ul>
          <h3 id="cnn-details">CNN Architecture</h3>
          <p>Details about the specific layers and training parameters used in the Convolutional Neural Network model...</p>
          <blockquote>"The integration of AI provides a significant leap in identification capabilities." - Dr. Levermore</blockquote>
          <h2 id="results">Results</h2>
          <p>Testing on diverse sample sets demonstrates a 98.5% accuracy rate, even with challenging polymer blends and degraded microplastics. Analysis time was reduced by an average of 80% compared to manual expert analysis.</p>
          <h2 id="conclusion">Conclusion</h2>
          <p>AI-powered spectral analysis offers a transformative solution for microplastic research and quality control. Plastic Analytics is committed to advancing this technology.</p>
        `,
        references: [
            { id: 1, text: 'Levermore, J. et al. (2025).  Forthcoming' },
            { id: 2, text: 'Levermore, J. et al. (2025).  Forthcoming' }
        ],
        recommendedArticleIds: [ 'environmental-monitoring', 'ml-advances', 'multimodal-analysis' ] // Example IDs
    };
   // Add more sample articles or fetch logic
   const allSampleArticles = {
       'ai-plastic-analysis': sampleArticle,
       'environmental-monitoring': { id: 'environmental-monitoring', slug: 'environmental-monitoring', title: 'AI in Environmental Monitoring', category: 'Research', date: 'Mar 24, 2025', author: { name: 'Dr. Joseph Levermore'}, imageSrc: 'https://via.placeholder.com/400x250?text=Env+Monitor', articlepagehref: 'environmental-monitoring' },
       'ml-advances': { id: 'ml-advances', slug: 'ml-advances', title: 'Machine Learning Advances Improve Accuracy', category: 'Technology', date: 'Feb 28, 2025', author: { name: 'Dr. Joseph Levermore'}, imageSrc: 'https://via.placeholder.com/400x250?text=ML+Advances', articlepagehref: 'ml-advances' },
       'multimodal-analysis': { id: 'multimodal-analysis', slug: 'multimodal-analysis', title: 'Combining FTIR and Raman with AI', category: 'Technology', date: 'Mar 15, 2025', author: { name: 'Dr. Joseph Levermore'}, imageSrc: 'https://via.placeholder.com/400x250?text=Multimodal', articlepagehref: 'multimodal-analysis' },
   }
   return allSampleArticles[slug] || null; // Return null if not found
};

const getRecommendedArticles = async (ids, allArticlesData) => {
   // Simulate fetching summaries for recommended articles
   // console.log"Fetching recommended:", ids);
   return ids.map(id => allArticlesData[id]).filter(Boolean); // Filter out any not found
};
// --- End Mock Data ---


const ArticlePage = () => {
  const { articleSlug } = useParams(); // Get slug from URL: e.g., /news/:articleSlug
  const [article, setArticle] = useState(null);
  const [recommended, setRecommended] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const fetchedArticle = await getArticleData(articleSlug);
        if (fetchedArticle) {
          setArticle(fetchedArticle);
          // In a real app, fetch all articles summary data separately
          const allArticlesData = await Promise.resolve({ /* Include all article summaries here */
            'ai-plastic-analysis': { id: 'ai-plastic-analysis', slug: 'ai-plastic-analysis', title: 'Putting AI to work...', /* more fields */ articlepagehref: 'ai-plastic-analysis' },
            'environmental-monitoring': { id: 'environmental-monitoring', slug: 'environmental-monitoring', title: 'AI in Environmental Monitoring', category: 'Research', date: 'Mar 24, 2025', author: { name: 'M. Rodriguez'}, imageSrc: 'https://via.placeholder.com/400x250?text=Env+Monitor', articlepagehref: 'environmental-monitoring' },
            'ml-advances': { id: 'ml-advances', slug: 'ml-advances', title: 'Machine Learning Advances Improve Accuracy', category: 'Technology', date: 'Feb 28, 2025', author: { name: 'J. Wilson'}, imageSrc: 'https://via.placeholder.com/400x250?text=ML+Advances', articlepagehref: 'ml-advances' },
            'multimodal-analysis': { id: 'multimodal-analysis', slug: 'multimodal-analysis', title: 'Combining FTIR and Raman with AI', category: 'Technology', date: 'Mar 15, 2025', author: { name: 'Dr. Joseph Levermore'}, imageSrc: 'https://via.placeholder.com/400x250?text=Multimodal', articlepagehref: 'multimodal-analysis' },
         });
          const fetchedRecommended = await getRecommendedArticles(fetchedArticle.recommendedArticleIds || [], allArticlesData);
          setRecommended(fetchedRecommended);
        } else {
          setError("Article not found.");
        }
      } catch (err) {
        console.error("Error loading article:", err);
        setError("Failed to load article.");
      } finally {
        setIsLoading(false);
      }
    };

    if (articleSlug) {
      loadData();
    } else {
        setError("No article specified.");
        setIsLoading(false);
    }
    // Scroll to top on slug change
    window.scrollTo(0, 0);
  }, [articleSlug]);

  // Define page layout styles
  const styles = {
    pageContainer: {
      ...commonStyles.container, // Apply base container styles
      paddingTop: '40px',
      paddingBottom: '60px',
    },
    layoutContainer: { // Uses Grid for main content + sticky sidebar
        display: 'grid',
        gridTemplateColumns: '1fr auto', // Main content takes available space, sidebar auto width
        gap: '60px', // Gap between content and sidebar
        alignItems: 'start', // Align items to the top
         // Responsive: Stack columns on smaller screens (requires JS/CSS)
        // '@media (max-width: 1024px)': { // Cannot do this directly inline
        //     gridTemplateColumns: '1fr',
        //     gap: '0',
        // },
    },
    mainContentArea: {
        gridColumn: '1 / 2', // Takes the first column
        // No specific style needed unless overriding defaults
    },
    sidebarArea: {
        gridColumn: '2 / 3', // Takes the second column
        // Hide on smaller screens (requires JS width check or CSS)
        // display: windowWidth > 1024 ? 'block' : 'none',
    },
     loadingOrError: {
        minHeight: '50vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1.2rem',
        color: colors.secondaryText,
     }
  };

  if (isLoading) {
    return <div style={styles.loadingOrError}>Loading article...</div>;
  }

  if (error) {
    return <div style={styles.loadingOrError}>{error}</div>;
  }

  if (!article) {
    return <div style={styles.loadingOrError}>Article data could not be loaded.</div>; // Fallback
  }

  return (
    <div style={styles.pageContainer}>
       <div style={styles.layoutContainer}>
           {/* Main Article Content Area */}
           <main style={styles.mainContentArea}>
                <ArticleHeader
                    title={article.title}
                    category={article.category}
                    date={article.date}
                    imageUrl={article.featuredImage?.src}
                    imageAlt={article.featuredImage?.alt}
                />
                <AuthorInfo author={article.author} date={article.date} />
                {/* Add className for observer targeting if needed */}
                <ArticleBody content={article.content} /* className="article-content-for-observer" */ />
                <References references={article.references} />
           </main>

            {/* Sticky Sidebar */}
            {/* Add conditional rendering based on screen width if needed */}
           <aside style={styles.sidebarArea}>
                <QuickLinks content={article.content} />
           </aside>
       </div>

       {/* Recommended Articles (Full Width Below Main Content) */}
       <RecommendedArticles articles={recommended} />
    </div>
  );
};

export default ArticlePage;